import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
    Box, Typography, Avatar, TextField, InputAdornment,
    Divider, Button, Dialog, DialogContent, Checkbox,
    IconButton, MenuItem, Select, FormControl, InputLabel,
    SelectChangeEvent, Tooltip, Alert, CircularProgress,
} from '@mui/material';
import {
    FaSearch, FaLinkedin, FaTrash, FaGraduationCap,
    FaBriefcase, FaBuilding, FaCalendarAlt, FaTimes,
    FaUserGraduate, FaExternalLinkAlt, FaMapMarkerAlt,
    FaChevronRight, FaArrowLeft, FaMedal, FaUniversity,
    FaEnvelope, FaExclamationTriangle, FaFileExcel,
    FaChevronLeft, FaCheckSquare, FaMinusSquare, FaSync,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Cookies from 'js-cookie';
import { Prevelage } from '../Scripts/rolePrivelageCheck';
import { alumniService } from './Alumni.service';

// ─── API ──────────────────────────────────────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

interface Alumni {
    uid: string;
    e_id: string;
    name: string;
    email: string;
    instituteCode: string;
    fac: number;
    graduationYear: number | null;
    jobTitle: string | null;
    company: string | null;
    sector: string | null;
    country: string | null;
    city: string | null;
    employmentStatus: string | null;
    highestQualification: string | null;
    recommendRating: number | null;
    satisfactionRating: number | null;
    feedback: string | null;
    salaryRange: string | null;
    salaryCurrency: string | null;
    linkedinLinked: boolean;
    linkedinUrl: string | null;
    linkedinUid: string | null;
    linkedinLinkedAt: number | null;
    status: string;
    addedAt: any;
    updatedAt: any;
}

// ─── Theme ────────────────────────────────────────────────────────────────────

const C = {
    dark: '#1a1c1e',
    accent: '#fbc02d',
    accentDim: 'rgba(251,192,45,0.10)',
    accentBorder: 'rgba(251,192,45,0.30)',
    text: '#1a1c1e',
    muted: '#6b7280',
    mutedLight: '#9ca3af',
    card: '#ffffff',
    border: 'rgba(0,0,0,0.07)',
    surface: '#f8f7f4',
    success: '#16a34a',
    successDim: 'rgba(22,163,74,0.09)',
    linkedin: '#0077B5',
    linkedinDim: 'rgba(0,119,181,0.09)',
    danger: '#dc2626',
    dangerDim: 'rgba(220,38,38,0.07)',
    warning: '#d97706',
    warningDim: 'rgba(217,119,6,0.09)',
};

const EMPLOYMENT_LABELS: Record<string, string> = {
    employed: 'Employed',
    self_employed: 'Self-Employed',
    higher_studies: 'Higher Studies',
    unemployed: 'Unemployed',
};

const PAGE_SIZE = 8;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name: string) {
    return name.trim().split(' ').map(w => w[ 0 ]).join('').toUpperCase().slice(0, 2);
}
function avatarBg(uid: string) {
    const p = [ '#7C3AED', '#0369A1', '#B45309', '#065F46', '#9D174D', '#1D4ED8', '#92400E' ];
    let h = 0;
    for (let i = 0; i < uid.length; i++) h = uid.charCodeAt(i) + ((h << 5) - h);
    return p[ Math.abs(h) % p.length ];
}
function fmtSalary(range: string | null, currency: string | null) {
    if (!range || range === '') return null;
    return `${currency ?? ''} ${range}`.trim();
}

// ─── Atoms ────────────────────────────────────────────────────────────────────

function SkillPill({ label }: { label: string }) {
    return (
        <Box sx={{ px: 1.2, py: 0.25, borderRadius: '5px', display: 'inline-flex', background: C.accentDim, border: `1px solid ${C.accentBorder}`, fontSize: 11, fontWeight: 600, color: '#78580a', whiteSpace: 'nowrap' }}>
            {label}
        </Box>
    );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography sx={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700, mb: 1.5, display: 'block' }}>
            {children}
        </Typography>
    );
}

function StatusBadge({ status }: { status: string | null }) {
    const map: Record<string, { label: string; color: string; dim: string }> = {
        employed: { label: 'Employed', color: C.success, dim: C.successDim },
        self_employed: { label: 'Self-Employed', color: '#7c3aed', dim: 'rgba(124,58,237,0.1)' },
        higher_studies: { label: 'Higher Studies', color: '#b45309', dim: 'rgba(180,83,9,0.1)' },
        unemployed: { label: 'Unemployed', color: C.muted, dim: C.surface },
    };
    const m = map[ status ?? '' ] ?? { label: 'Unknown', color: C.muted, dim: C.surface };
    return (
        <Box sx={{ px: 1, py: 0.15, borderRadius: '4px', display: 'inline-flex', background: m.dim, border: `1px solid ${m.color}40`, fontSize: 10, fontWeight: 700, color: m.color, whiteSpace: 'nowrap' }}>
            {m.label}
        </Box>
    );
}

function StarRow({ value, label }: { value: number | null; label: string }) {
    if (!value) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: C.muted, minWidth: 110 }}>{label}</Typography>
            <Box sx={{ display: 'flex', gap: 0.3 }}>
                {[ 1, 2, 3, 4, 5 ].map(i => (
                    <Box key={i} sx={{ width: 10, height: 10, borderRadius: '2px', background: i <= value ? C.accent : C.border }} />
                ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: C.muted }}>{value}/5</Typography>
        </Box>
    );
}

// ─── Alumni Card ──────────────────────────────────────────────────────────────

function AlumniCard({ alumni, selected, checked, onSelect, onCheck, canDelete }: {
    alumni: Alumni; selected: boolean; checked: boolean;
    onSelect: (a: Alumni) => void; onCheck: (uid: string) => void; canDelete: boolean;
}) {
    const bg = avatarBg(alumni.uid);
    return (
        <Box onClick={() => onSelect(alumni)} sx={{
            background: C.card,
            border: `1.5px solid ${selected ? C.accent : checked ? 'rgba(220,38,38,0.4)' : C.border}`,
            borderRadius: '14px', p: 2.5, cursor: 'pointer', position: 'relative',
            transition: 'all 0.16s ease',
            boxShadow: selected ? `0 0 0 3px ${C.accentDim}` : checked ? '0 0 0 3px rgba(220,38,38,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
            '&:hover': { borderColor: checked ? C.danger : C.accent, boxShadow: `0 4px 18px rgba(251,192,45,0.14)`, transform: 'translateY(-2px)' },
        }}>
            {canDelete && (
                <Box sx={{ position: 'absolute', top: 8, left: 8 }} onClick={e => { e.stopPropagation(); onCheck(alumni.uid); }}>
                    <Checkbox checked={checked} size="small" sx={{ p: 0.3, color: C.mutedLight, '&.Mui-checked': { color: C.danger } }} />
                </Box>
            )}
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                {alumni.graduationYear && (
                    <Box sx={{ px: 1, py: 0.2, borderRadius: '5px', background: C.accentDim, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 700, color: '#78580a' }}>
                        '{String(alumni.graduationYear).slice(2)}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start', pl: canDelete ? 3.5 : 0, pr: 4 }}>
                <Avatar sx={{ width: 46, height: 46, background: bg, fontSize: 15, fontWeight: 700, flexShrink: 0, border: `2px solid ${C.accent}` }}>
                    {initials(alumni.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.2 }}>{alumni.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: C.muted }}>{alumni.e_id}</Typography>
                    <Box sx={{ mt: 0.4 }}>
                        <StatusBadge status={alumni.employmentStatus} />
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ borderColor: C.border, mb: 1.5 }} />

            {alumni.jobTitle || alumni.company ? (
                <Box sx={{ mb: 1.2 }}>
                    {alumni.jobTitle && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
                            <FaBriefcase size={10} color={C.muted} />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>{alumni.jobTitle}</Typography>
                        </Box>
                    )}
                    {alumni.company && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <FaBuilding size={10} color={C.muted} />
                            <Typography sx={{ fontSize: 11, color: C.muted }}>{alumni.company}</Typography>
                            {alumni.city && <><Typography sx={{ fontSize: 11, color: C.mutedLight }}>·</Typography><Typography sx={{ fontSize: 11, color: C.mutedLight }}>{alumni.city}</Typography></>}
                        </Box>
                    )}
                </Box>
            ) : (
                <Typography sx={{ fontSize: 12, color: C.mutedLight, fontStyle: 'italic', mb: 1.2 }}>No employment data</Typography>
            )}

            {alumni.sector && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
                    <FaUniversity size={10} color={C.muted} />
                    <Typography sx={{ fontSize: 11, color: C.muted }}>{alumni.sector}</Typography>
                </Box>

            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
                <FaMedal size={10} color={C.muted} />

                <Typography sx={{ fontSize: 11, color: C.muted }}>
                    {fmtSalary(alumni.salaryRange, alumni.salaryCurrency) || 'Package not provided'}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {alumni.linkedinLinked && alumni.linkedinUrl ? (
                    <Box component="a" href={alumni.linkedinUrl} target="_blank" rel="noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.1, py: 0.35, borderRadius: '6px', textDecoration: 'none', background: C.linkedinDim, border: '1px solid rgba(0,119,181,0.22)', color: C.linkedin, fontSize: 11, fontWeight: 600, '&:hover': { background: 'rgba(0,119,181,0.16)' }, transition: 'background 0.14s' }}>
                        <FaLinkedin size={11} /> LinkedIn <FaExternalLinkAlt size={9} />
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 11, color: C.mutedLight, fontStyle: 'italic' }}>No LinkedIn</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: C.mutedLight }}>
                    <Typography sx={{ fontSize: 11 }}>Profile</Typography>
                    <FaChevronRight size={9} />
                </Box>
            </Box>
        </Box>
    );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function DetailPanel({ alumni, onClose, canDelete, onDelete }: {
    alumni: Alumni; onClose: () => void; canDelete: boolean; onDelete: (a: Alumni) => void;
}) {
    const bg = avatarBg(alumni.uid);
    const salary = fmtSalary(alumni.salaryRange, alumni.salaryCurrency);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ background: C.dark, px: 2.5, pt: 2.5, pb: 2 }}>
                <Box onClick={onClose} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, mb: 2, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 12, '&:hover': { color: 'rgba(255,255,255,0.7)' }, transition: 'color 0.14s' }}>
                    <FaArrowLeft size={11} /> Directory
                </Box>
                <Box sx={{ display: 'flex', gap: 1.8, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 64, height: 64, background: bg, fontSize: 22, fontWeight: 700, flexShrink: 0, border: `2.5px solid ${C.accent}` }}>{initials(alumni.name)}</Avatar>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.1, mb: 0.3 }}>{alumni.name}</Typography>
                        {alumni.jobTitle && alumni.company && (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mb: 0.4 }}>{alumni.jobTitle} at {alumni.company}</Typography>
                        )}
                        <StatusBadge status={alumni.employmentStatus} />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    {[
                        { label: 'E-ID', value: alumni.e_id },
                        { label: 'Graduated', value: alumni.graduationYear ? String(alumni.graduationYear) : '—' },
                        { label: 'Country', value: alumni.country ?? '—' },
                        { label: 'Rating', value: alumni.recommendRating ? `${alumni.recommendRating}/5` : '—' },
                    ].map((s, i) => (
                        <Box key={s.label} sx={{ flex: 1, pl: i > 0 ? 1.5 : 0, ml: i > 0 ? 1.5 : 0, borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                            <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2 }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{s.value}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>

                {/* LinkedIn */}
                {alumni.linkedinLinked && alumni.linkedinUrl && (
                    <Box component="a" href={alumni.linkedinUrl} target="_blank" rel="noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.8, mb: 2.5, borderRadius: '12px', textDecoration: 'none', background: C.linkedinDim, border: '1px solid rgba(0,119,181,0.22)', '&:hover': { background: 'rgba(0,119,181,0.15)' }, transition: 'background 0.14s' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: '9px', background: C.linkedin, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaLinkedin size={18} color="#fff" /></Box>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.linkedin }}>View LinkedIn Profile</Typography>
                                <Typography sx={{ fontSize: 11, color: C.muted }}>{alumni.linkedinUrl.replace('https://', '')}</Typography>
                            </Box>
                        </Box>
                        <FaExternalLinkAlt size={13} color={C.linkedin} />
                    </Box>
                )}

                {/* Employment */}
                <Box sx={{ mb: 2.5 }}>
                    <SectionLabel>Employment</SectionLabel>
                    <Box sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {[
                            { label: 'Status', value: EMPLOYMENT_LABELS[ alumni.employmentStatus ?? '' ] ?? alumni.employmentStatus },
                            { label: 'Job Title', value: alumni.jobTitle },
                            { label: 'Company', value: alumni.company },
                            { label: 'Sector', value: alumni.sector },
                            { label: 'Location', value: [ alumni.city, alumni.country ].filter(Boolean).join(', ') || null },
                            { label: 'Package', value: salary },
                        ].map(row => row.value ? (
                            <Box key={row.label} sx={{ display: 'flex', gap: 1 }}>
                                <Typography sx={{ fontSize: 11, color: C.muted, minWidth: 80 }}>{row.label}</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>{row.value}</Typography>
                            </Box>
                        ) : null)}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: C.border, my: 2 }} />

                {/* Education */}
                <Box sx={{ mb: 2.5 }}>
                    <SectionLabel>Education</SectionLabel>
                    <Box sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {[
                            { label: 'Grad Year', value: alumni.graduationYear ? String(alumni.graduationYear) : null },
                            { label: 'Qualification', value: alumni.highestQualification },
                        ].map(row => row.value ? (
                            <Box key={row.label} sx={{ display: 'flex', gap: 1 }}>
                                <Typography sx={{ fontSize: 11, color: C.muted, minWidth: 80 }}>{row.label}</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>{row.value}</Typography>
                            </Box>
                        ) : null)}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: C.border, my: 2 }} />

                {/* Feedback */}
                {(alumni.recommendRating || alumni.satisfactionRating || alumni.feedback) && (
                    <>
                        <Box sx={{ mb: 2.5 }}>
                            <SectionLabel>Survey Feedback</SectionLabel>
                            <StarRow value={alumni.recommendRating} label="Recommendation" />
                            <StarRow value={alumni.satisfactionRating} label="Satisfaction" />
                            {alumni.feedback && (
                                <Box sx={{ mt: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', p: 1.2 }}>
                                    <Typography sx={{ fontSize: 12, color: C.text, lineHeight: 1.6, fontStyle: 'italic' }}>"{alumni.feedback}"</Typography>
                                </Box>
                            )}
                        </Box>
                        <Divider sx={{ borderColor: C.border, my: 2 }} />
                    </>
                )}

                {/* Contact */}
                <Box>
                    <SectionLabel>Contact</SectionLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaEnvelope size={12} color={C.muted} />
                        <Typography component="a" href={`mailto:${alumni.email}`} sx={{ fontSize: 13, color: C.linkedin, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{alumni.email}</Typography>
                    </Box>
                </Box>
            </Box>

            {canDelete && (
                <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${C.border}` }}>
                    <Button fullWidth startIcon={<FaTrash size={11} />} onClick={() => onDelete(alumni)}
                        sx={{ textTransform: 'none', color: C.danger, fontSize: 13, border: `1px solid rgba(220,38,38,0.2)`, borderRadius: '10px', py: 1, '&:hover': { background: 'rgba(220,38,38,0.05)', borderColor: C.danger } }}>
                        Remove Alumni Record
                    </Button>
                </Box>
            )}
        </Box>
    );
}

// ─── Single Delete Dialog ─────────────────────────────────────────────────────

function SingleDeleteDialog({ alumni, onClose, onConfirm }: {
    alumni: Alumni | null; onClose: () => void; onConfirm: () => void;
}) {
    const [ typed, setTyped ] = useState('');
    const expected = alumni?.name ?? '';
    return (
        <Dialog open={!!alumni} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
            <Box sx={{ background: C.dark, px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaExclamationTriangle size={14} color={C.danger} />
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>Remove Alumni Record</Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}><FaTimes size={13} /></IconButton>
            </Box>
            <DialogContent sx={{ pt: 2.5 }}>
                <Box sx={{ background: C.dangerDim, border: `1px solid rgba(220,38,38,0.25)`, borderRadius: '10px', p: 1.5, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 12, color: C.danger, lineHeight: 1.6 }}>
                        <strong>Permanent and irreversible.</strong> This alumni record cannot be recovered once deleted.
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: C.muted, mb: 2, lineHeight: 1.6 }}>
                    Deleting record for <Box component="span" sx={{ fontWeight: 700, color: C.text }}>{alumni?.name}</Box> ({alumni?.e_id}).
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.8 }}>
                    Type <Box component="span" sx={{ fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>{expected}</Box> to confirm:
                </Typography>
                <TextField fullWidth size="small" value={typed} onChange={e => setTyped(e.target.value)} placeholder={expected} sx={{ mb: 0.5 }} />
                {typed && typed !== expected && <Typography sx={{ fontSize: 11, color: C.danger }}>Name doesn't match</Typography>}
            </DialogContent>
            <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 1.5 }}>
                <Button fullWidth onClick={() => { onClose(); setTyped(''); }} sx={{ textTransform: 'none', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '10px' }}>Cancel</Button>
                <Button fullWidth onClick={() => { onConfirm(); setTyped(''); }} disabled={typed !== expected} variant="contained"
                    sx={{ textTransform: 'none', background: C.danger, borderRadius: '10px', '&:hover': { background: '#b91c1c' }, '&:disabled': { background: 'rgba(220,38,38,0.3)' } }}>
                    Permanently Delete
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Bulk Delete Dialog ───────────────────────────────────────────────────────

function BulkDeleteDialog({ count, names, onClose, onConfirm }: {
    count: number; names: string[]; onClose: () => void; onConfirm: () => void;
}) {
    const [ typed, setTyped ] = useState('');
    const expected = `DELETE ${count}`;
    return (
        <Dialog open={count > 0} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
            <Box sx={{ background: C.dark, px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaExclamationTriangle size={14} color={C.danger} />
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>Bulk Delete — {count} Records</Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)' }}><FaTimes size={13} /></IconButton>
            </Box>
            <DialogContent sx={{ pt: 2.5 }}>
                <Box sx={{ background: C.dangerDim, border: `1px solid rgba(220,38,38,0.25)`, borderRadius: '10px', p: 1.5, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 12, color: C.danger, lineHeight: 1.6 }}>
                        <strong>Permanent and irreversible.</strong> {count} alumni records will be deleted forever.
                    </Typography>
                </Box>
                <Box sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', p: 1.5, mb: 2.5, maxHeight: 120, overflowY: 'auto' }}>
                    {names.map((n, i) => <Typography key={i} sx={{ fontSize: 12, color: C.text, py: 0.3, borderBottom: i < names.length - 1 ? `1px solid ${C.border}` : 'none' }}>{n}</Typography>)}
                </Box>
                <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.8 }}>
                    Type <Box component="span" sx={{ fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>{expected}</Box> to confirm:
                </Typography>
                <TextField fullWidth size="small" value={typed} onChange={e => setTyped(e.target.value)} placeholder={expected} />
            </DialogContent>
            <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 1.5 }}>
                <Button fullWidth onClick={() => { onClose(); setTyped(''); }} sx={{ textTransform: 'none', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '10px' }}>Cancel</Button>
                <Button fullWidth onClick={() => { onConfirm(); setTyped(''); }} disabled={typed !== expected} variant="contained"
                    sx={{ textTransform: 'none', background: C.danger, borderRadius: '10px', '&:hover': { background: '#b91c1c' }, '&:disabled': { background: 'rgba(220,38,38,0.3)' } }}>
                    Delete {count} Records
                </Button>
            </Box>
        </Dialog>
    );
}

// ─── Batch Delete Panel ───────────────────────────────────────────────────────

interface ExcelRow { [ key: string ]: string | number }

function BatchDeletePanel({ onDelete }: { onDelete: (eIds: string[]) => void }) {
    const fileRef = useRef<HTMLInputElement>(null);
    const [ stage, setStage ] = useState<'upload' | 'map'>('upload');
    const [ columns, setColumns ] = useState<string[]>([]);
    const [ rows, setRows ] = useState<ExcelRow[]>([]);
    const [ selectedCol, setSelectedCol ] = useState('');
    const [ fileName, setFileName ] = useState('');

    const reset = () => {
        setStage('upload'); setColumns([]); setRows([]);
        setSelectedCol(''); setFileName('');
    };

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[ 0 ];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { alert("Max 2MB"); e.target.value = ''; return; }
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => {
            const wb = XLSX.read(ev.target?.result, { type: 'binary' });
            const ws = wb.Sheets[ wb.SheetNames[ 0 ] ];
            const data: ExcelRow[] = XLSX.utils.sheet_to_json(ws);
            if (!data.length) return;
            if (data.length > 2000) { alert("Max 2000 rows"); e.target.value = ''; return; }
            setRows(data);
            setColumns(Object.keys(data[ 0 ]));
            setStage('map');
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const handleConfirm = () => {
        const eIds = rows
            .map(row => String(row[ selectedCol ] ?? '').trim())
            .filter(Boolean);
        onDelete(eIds);
        reset();
    };

    return (
        <Box sx={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px', p: 3, width: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FaFileExcel size={16} color={C.success} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.text }}>Batch Delete via Excel / CSV</Typography>
            </Box>

            {stage === 'upload' && (
                <Box>
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
                    <Button fullWidth variant="outlined" startIcon={<FaFileExcel size={13} />} onClick={() => fileRef.current?.click()}
                        sx={{ textTransform: 'none', borderColor: C.success, color: C.success, borderRadius: '10px', py: 1.5, fontSize: 13, borderStyle: 'dashed', '&:hover': { borderColor: C.success, background: C.successDim } }}>
                        Upload Excel / CSV File
                    </Button>
                    <Typography sx={{ fontSize: 11, color: C.mutedLight, textAlign: 'center', mt: 1 }}>
                        Supported: .xlsx · .xls · .csv · Max 2MB · Max 2,000 rows
                    </Typography>
                </Box>
            )}

            {stage === 'map' && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: C.success }} />
                        <Typography sx={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{fileName}</Typography>
                        <Typography sx={{ fontSize: 12, color: C.muted }}>— {rows.length} rows</Typography>
                        <IconButton size="small" onClick={reset} sx={{ ml: 'auto', color: C.muted }}><FaTimes size={12} /></IconButton>
                    </Box>

                    <Typography sx={{ fontSize: 13, color: C.text, fontWeight: 600, mb: 0.5 }}>Which column contains the E-ID?</Typography>
                    <Box sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', p: 1.5, mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, pb: 1, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
                            {columns.map(col => (
                                <Box key={col} onClick={() => setSelectedCol(col)} sx={{
                                    px: 1.5, py: 0.5, borderRadius: '6px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    background: selectedCol === col ? C.accentDim : C.card,
                                    border: `1.5px solid ${selectedCol === col ? C.accent : C.border}`,
                                    color: selectedCol === col ? '#78580a' : C.text,
                                    transition: 'all 0.12s',
                                }}>
                                    {col}
                                </Box>
                            ))}
                        </Box>
                        {selectedCol && rows.slice(0, 3).map((row, i) => (
                            <Typography key={i} sx={{ fontSize: 11, color: C.muted, py: 0.3 }}>
                                Sample: <Box component="span" sx={{ fontWeight: 600, color: C.text }}>{String(row[ selectedCol ])}</Box>
                            </Typography>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button onClick={reset} sx={{ textTransform: 'none', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '10px', flex: 1 }}>Cancel</Button>
                        <Button onClick={handleConfirm} disabled={!selectedCol} variant="contained"
                            sx={{ textTransform: 'none', background: C.danger, color: '#fff', borderRadius: '10px', flex: 1, fontWeight: 700, '&:hover': { background: '#b91c1c' }, '&:disabled': { background: C.accentDim } }}>
                            Delete Records
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, total, pageSize, onChange }: { page: number; total: number; pageSize: number; onChange: (p: number) => void }) {
    const pages = Math.ceil(total / pageSize);
    if (pages <= 1) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3 }}>
            <IconButton size="small" onClick={() => onChange(page - 1)} disabled={page === 1} sx={{ border: `1px solid ${C.border}`, borderRadius: '8px', color: page === 1 ? C.mutedLight : C.text }}>
                <FaChevronLeft size={11} />
            </IconButton>
            {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                <Box key={p} onClick={() => onChange(p)} sx={{
                    width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: 13, fontWeight: p === page ? 700 : 400,
                    background: p === page ? C.accent : 'transparent',
                    color: p === page ? C.dark : C.muted,
                    border: p === page ? 'none' : `1px solid ${C.border}`,
                    '&:hover': { background: p === page ? C.accent : C.surface },
                    transition: 'all 0.12s',
                }}>
                    {p}
                </Box>
            ))}
            {pages > 7 && <Typography sx={{ fontSize: 12, color: C.muted }}>...</Typography>}
            <IconButton size="small" onClick={() => onChange(page + 1)} disabled={page === pages} sx={{ border: `1px solid ${C.border}`, borderRadius: '8px', color: page === pages ? C.mutedLight : C.text }}>
                <FaChevronRight size={11} />
            </IconButton>
        </Box>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type Tab = 'directory' | 'batch';

export default function AlumniPage() {
    const userRole = (Cookies.get('role') || 'faculty').toLowerCase();
    const roleService = new Prevelage(userRole);
    const canDelete = roleService.canDeleteUser();
    const instituteCode = Cookies.get('parent') || '';

    // ── data ─────────────────────────────────────────────────────────
    const [ records, setRecords ] = useState<Alumni[]>([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState<string | null>(null);
    const [ nextCursor, setNextCursor ] = useState<string | null>(null);
    const [ hasMore, setHasMore ] = useState(false);

    // ── filters ───────────────────────────────────────────────────────
    const [ search, setSearch ] = useState('');
    const [ sectorFilter, setSectorFilter ] = useState('');
    const [ yearFilter, setYearFilter ] = useState('');
    const [ employmentFilter, setEmploymentFilter ] = useState('');
    const [ linkedinFilter, setLinkedinFilter ] = useState('');
    const [ countryFilter, setCountryFilter ] = useState('');

    // ── ui ────────────────────────────────────────────────────────────
    const [ selected, setSelected ] = useState<Alumni | null>(null);
    const [ page, setPage ] = useState(1);
    const [ checked, setChecked ] = useState<Set<string>>(new Set());
    const [ toDelete, setToDelete ] = useState<Alumni | null>(null);
    const [ bulkOpen, setBulkOpen ] = useState(false);
    const [ tab, setTab ] = useState<Tab>('directory');


    const [ deleteStatus, setDeleteStatus ] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [ deleteMessage, setDeleteMessage ] = useState('');

    const [ filters, setFilters ] = useState<{
        sectors: string[];
        years: number[];
        countries: string[];
        statuses: string[];
        total: number;
    }>({ sectors: [], years: [], countries: [], statuses: [], total: 0 });

    // ── fetch ─────────────────────────────────────────────────────────
    const load = useCallback(async (cursor: string | null = null, replace = true) => {
        setLoading(true);
        setError(null);
        try {
            const data = await alumniService.getAll({
                instituteCode,
                limit: 20,
                startAfter: cursor,
                search: search || undefined,
                employmentStatus: employmentFilter || undefined,
                graduationYear: yearFilter || undefined,
                sector: sectorFilter || undefined,
                linkedinLinked: linkedinFilter || undefined,
                country: countryFilter || undefined,
            });
            if (!data.success) throw new Error(data.error || 'Failed to fetch alumni');
            setRecords(prev => replace ? data.alumni : [ ...prev, ...data.alumni ]);
            setNextCursor(data.nextCursor);
            setHasMore(data.hasMore);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [ instituteCode, search, employmentFilter, yearFilter, sectorFilter, linkedinFilter, countryFilter ]);

    // initial load + filter changes (debounce search)
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            setNextCursor(null);
            load(null, true);
        }, search ? 400 : 0);
        return () => clearTimeout(t);
    }, [ search, employmentFilter, yearFilter, sectorFilter, linkedinFilter, countryFilter ]);

    // filters 
    useEffect(() => {
        alumniService.getFilters(instituteCode)
            .then(d => { if (d.success) setFilters(d); })
            .catch(console.error);
    }, [ instituteCode ]);

    // load more when paginating past current records
    const paginated = records.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    useEffect(() => {
        const needed = page * PAGE_SIZE;
        if (needed > records.length && hasMore && !loading) load(nextCursor, false);
    }, [ page ]);

    // ── derived ───────────────────────────────────────────────────────

    const employed = records.filter(a => a.employmentStatus === 'employed' || a.employmentStatus === 'self_employed').length;
    const higherStudies = records.filter(a => a.employmentStatus === 'higher_studies').length;
    const withLinkedIn = records.filter(a => a.linkedinLinked).length;
    const allPageChecked = paginated.length > 0 && paginated.every(a => checked.has(a.uid));
    const somePageChecked = paginated.some(a => checked.has(a.uid)) && !allPageChecked;
    const hasFilters = !!(search || sectorFilter || yearFilter || employmentFilter || linkedinFilter || countryFilter);

    const clearFilters = () => {
        setSearch(''); setSectorFilter(''); setYearFilter('');
        setEmploymentFilter(''); setLinkedinFilter(''); setCountryFilter('');
        setPage(1); setChecked(new Set());
    };

    const toggleCheck = useCallback((uid: string) => {
        setChecked(prev => { const n = new Set(prev); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
    }, []);

    const togglePageAll = () => {
        setChecked(prev => {
            const n = new Set(prev);
            if (allPageChecked) paginated.forEach(a => n.delete(a.uid));
            else paginated.forEach(a => n.add(a.uid));
            return n;
        });
    };

    // ── delete — TODO wire to real API ────────────────────────────────
    const deleteOne = async (alumni: Alumni) => {
        setDeleteStatus('loading');
        try {
            const result = await alumniService.delete([ alumni.uid ], instituteCode);
            if (!result.success) throw new Error(result.error || 'Delete failed');
            setRecords(prev => prev.filter(a => a.uid !== alumni.uid));
            if (selected?.uid === alumni.uid) setSelected(null);
            setToDelete(null);
            setDeleteStatus('success');
            setDeleteMessage('Alumni record removed successfully.');
        } catch (err: any) {
            setDeleteStatus('error');
            setDeleteMessage(err.message || 'Failed to delete record.');
        }
    };

    const deleteBulk = async () => {
        const count = checked.size;
        setDeleteStatus('loading');
        try {
            const result = await alumniService.delete(Array.from(checked), instituteCode);
            if (!result.success) throw new Error(result.error || 'Delete failed');
            setRecords(prev => prev.filter(a => !checked.has(a.uid)));
            if (selected && checked.has(selected.uid)) setSelected(null);
            setChecked(new Set());
            setBulkOpen(false);
            setDeleteStatus('success');
            setDeleteMessage(`${count} records deleted successfully.`);
        } catch (err: any) {
            setDeleteStatus('error');
            setDeleteMessage(err.message || 'Failed to delete records.');
        }
    };

    const deleteFromExcel = async (eIds: string[]) => {
        setDeleteStatus('loading');
        try {
            const result = await alumniService.deleteByEIds(eIds, instituteCode);
            if (!result.success) throw new Error(result.error || 'Delete failed');
            load(null, true);
            if (selected) setSelected(null);
            setChecked(new Set());
            setDeleteStatus('success');
            setDeleteMessage(`${result.deleted} deleted.${result.notFound > 0 ? ` ${result.notFound} not found.` : ''}`);
        } catch (err: any) {
            setDeleteStatus('error');
            setDeleteMessage(err.message || 'Failed to delete records.');
        }
    };

    const handlePageChange = (p: number) => { setPage(p); setSelected(null); };

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', boxSizing: 'border-box', gap: 2.5, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

            {/* ── Left panel ── */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: C.accentDim, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaUserGraduate size={16} color={C.accent} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1 }}>Alumni Directory</Typography>
                        <Typography sx={{ fontSize: 12, color: C.muted }}>
                            {loading ? 'Loading...' : `${filters.total || records.length} graduates on record`}
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => load(null, true)} disabled={loading}
                            sx={{ border: `1px solid ${C.border}`, borderRadius: '8px', color: C.muted }}>
                            {loading ? <CircularProgress size={14} /> : <FaSync size={12} />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>{error}</Alert>}
                {deleteStatus !== 'idle' && (
                    <Alert
                        severity={deleteStatus === 'loading' ? 'info' : deleteStatus === 'success' ? 'success' : 'error'}
                        onClose={() => setDeleteStatus('idle')}
                        sx={{ mb: 2, borderRadius: '10px' }}
                        icon={deleteStatus === 'loading' ? <CircularProgress size={16} /> : undefined}
                    >
                        {deleteStatus === 'loading' ? 'Deleting records...' : deleteMessage}
                    </Alert>
                )}
                {/* Stats */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Total', value: records.length, color: C.accent },
                        { label: 'Employed', value: employed, color: C.success },
                        { label: 'Higher Studies', value: higherStudies, color: '#b45309' },
                        { label: 'On LinkedIn', value: withLinkedIn, color: C.linkedin },
                    ].map(s => (
                        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', px: 2, py: 1.2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
                            <Typography sx={{ fontSize: 12, color: C.muted }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.text }}>{s.value}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Tabs */}
                <Box sx={{ display: 'flex', mb: 2.5, background: C.surface, borderRadius: '10px', p: 0.5, alignSelf: 'flex-start', border: `1px solid ${C.border}` }}>
                    {([ 'directory', ...(canDelete ? [ 'batch' ] : []) ] as Tab[]).map(t => (
                        <Box key={t} onClick={() => setTab(t)} sx={{
                            px: 2, py: 0.8, borderRadius: '8px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            background: tab === t ? C.card : 'transparent',
                            color: tab === t ? C.text : C.muted,
                            border: tab === t ? `1px solid ${C.border}` : '1px solid transparent',
                            transition: 'all 0.14s',
                        }}>
                            {t === 'batch' ? '⚡ Batch Delete' : 'Directory'}
                        </Box>
                    ))}
                </Box>

                {/* Batch tab */}
                {tab === 'batch' && canDelete ? (
                    <BatchDeletePanel onDelete={deleteFromExcel} />
                ) : (
                    <>
                        {/* Filter bar */}
                        <Box sx={{ display: 'flex', gap: 1.2, mb: 2, flexWrap: 'wrap', background: C.card, p: 1.5, borderRadius: '12px', border: `1px solid ${C.border}` }}>
                            <TextField
                                placeholder="Search name, ID, company, email…"
                                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                                size="small" sx={{ flex: 2, minWidth: 200 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><FaSearch size={12} color={C.muted} /></InputAdornment>,
                                    endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearch(''); setPage(1); }} sx={{ p: 0.4 }}><FaTimes size={10} /></IconButton></InputAdornment> : null,
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={employmentFilter} label="Status" onChange={(e: SelectChangeEvent) => { setEmploymentFilter(e.target.value); setPage(1); }}>
                                    <MenuItem value="">All Statuses</MenuItem>
                                    {filters.statuses.map(s => (
                                        <MenuItem key={s} value={s}>{EMPLOYMENT_LABELS[ s ] ?? s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Sector</InputLabel>
                                <Select value={sectorFilter} label="Sector" onChange={(e: SelectChangeEvent) => { setSectorFilter(e.target.value); setPage(1); }}>
                                    <MenuItem value="">All Sectors</MenuItem>
                                    {filters.sectors.map(s => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 115 }}>
                                <InputLabel>Grad Year</InputLabel>
                                <Select value={yearFilter} label="Grad Year" onChange={(e: SelectChangeEvent) => { setYearFilter(e.target.value); setPage(1); }}>
                                    <MenuItem value="">All Years</MenuItem>
                                    {filters.years.map(y => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 115 }}>
                                <InputLabel>LinkedIn</InputLabel>
                                <Select value={linkedinFilter} label="LinkedIn" onChange={(e: SelectChangeEvent) => { setLinkedinFilter(e.target.value); setPage(1); }}>
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="true">Linked</MenuItem>
                                    <MenuItem value="false">Not Linked</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 115 }}>
                                <InputLabel>Country</InputLabel>
                                <Select value={countryFilter} label="Country" onChange={(e: SelectChangeEvent) => { setCountryFilter(e.target.value); setPage(1); }}>
                                    <MenuItem value="">All Countries</MenuItem>
                                    {filters.countries.map(c => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {hasFilters && (
                                <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', color: C.muted, fontSize: 12, px: 1, border: `1px solid ${C.border}`, borderRadius: '8px' }}>
                                    Clear all
                                </Button>
                            )}
                        </Box>

                        {/* Bulk toolbar */}
                        {canDelete && checked.size > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, px: 2, py: 1.2, background: C.dangerDim, border: `1px solid rgba(220,38,38,0.2)`, borderRadius: '10px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <FaExclamationTriangle size={12} color={C.danger} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.danger }}>{checked.size} selected</Typography>
                                </Box>
                                <Button size="small" startIcon={<FaTrash size={11} />} onClick={() => setBulkOpen(true)}
                                    sx={{ textTransform: 'none', color: C.danger, fontSize: 12, border: `1px solid rgba(220,38,38,0.3)`, borderRadius: '8px', ml: 'auto', '&:hover': { background: 'rgba(220,38,38,0.08)' } }}>
                                    Delete Selected
                                </Button>
                                <Button size="small" onClick={() => setChecked(new Set())} sx={{ textTransform: 'none', color: C.muted, fontSize: 12, border: `1px solid ${C.border}`, borderRadius: '8px' }}>
                                    Clear
                                </Button>
                            </Box>
                        )}

                        {/* Select all on page */}
                        {canDelete && paginated.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Tooltip title={allPageChecked ? 'Deselect page' : 'Select all on this page'}>
                                    <IconButton size="small" onClick={togglePageAll} sx={{ color: somePageChecked || allPageChecked ? C.danger : C.mutedLight }}>
                                        {allPageChecked ? <FaCheckSquare size={14} /> : somePageChecked ? <FaMinusSquare size={14} /> : <FaCheckSquare size={14} />}
                                    </IconButton>
                                </Tooltip>
                                <Typography sx={{ fontSize: 12, color: C.muted }}>
                                    {allPageChecked ? 'Deselect page' : `Select all ${paginated.length} on this page`}
                                </Typography>
                                {hasFilters && (
                                    <Typography sx={{ fontSize: 12, color: C.muted, ml: 'auto' }}>{records.length} result{records.length !== 1 ? 's' : ''}</Typography>
                                )}
                            </Box>
                        )}

                        {/* Loading skeleton */}
                        {loading && records.length === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                                <CircularProgress sx={{ color: C.accent }} />
                            </Box>
                        )}

                        {/* Empty state */}
                        {!loading && records.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 10 }}>
                                <FaGraduationCap size={36} color={C.muted} style={{ opacity: 0.2 }} />
                                <Typography sx={{ color: C.muted, mt: 2, fontSize: 14 }}>
                                    {hasFilters ? 'No alumni match your filters' : 'No alumni records yet'}
                                </Typography>
                                {hasFilters && <Button size="small" onClick={clearFilters} sx={{ mt: 1, textTransform: 'none', color: C.accent }}>Clear filters</Button>}
                            </Box>
                        )}

                        {/* Grid */}
                        {records.length > 0 && (
                            <>
                                <Box sx={{ display: 'grid', gridTemplateColumns: selected ? '1fr' : '1fr 1fr', gap: 2, overflowY: 'auto', pr: 0.5 }}>
                                    {paginated.map(a => (
                                        <AlumniCard key={a.uid} alumni={a} selected={selected?.uid === a.uid} checked={checked.has(a.uid)} onSelect={setSelected} onCheck={toggleCheck} canDelete={canDelete} />
                                    ))}
                                </Box>
                                <Pagination page={page} total={records.length} pageSize={PAGE_SIZE} onChange={handlePageChange} />
                                {hasMore && (
                                    <Typography sx={{ textAlign: 'center', fontSize: 11, color: C.mutedLight, mt: 1 }}>
                                        More records available — scroll to next page to load
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>

            {/* ── Right: Detail panel ── */}
            {selected && (
                <Box sx={{ width: 370, flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.07)', position: 'sticky', top: 0, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
                    <DetailPanel alumni={selected} onClose={() => setSelected(null)} canDelete={canDelete} onDelete={setToDelete} />
                </Box>
            )}

            {/* Dialogs */}
            <SingleDeleteDialog alumni={toDelete} onClose={() => setToDelete(null)} onConfirm={() => toDelete && deleteOne(toDelete)} />
            <BulkDeleteDialog
                count={bulkOpen ? checked.size : 0}
                names={records.filter(a => checked.has(a.uid)).map(a => `${a.name} — ${a.e_id}`)}
                onClose={() => setBulkOpen(false)}
                onConfirm={deleteBulk}
            />
        </Box>
    );
}